import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { ConfirmDialogComponent, HlmDialogOpener, type ConfirmDialogContext } from '@lite-workspace/shared/helm-ui/dialog';
import { ProjectRoleService } from '../../../core/project-role';
import { VerdictBudget } from '../../../core/route-wait';
import { ProjectsFacade } from '../domain/application/projects.facade';
import { demoBuildState, type DemoBuildState } from '../domain/utils/project.utils';
import { DEMO_RETRY_LABEL } from '../ui/project-list-item';

const PREPARING_NOTICE: ConfirmDialogContext = {
  title: 'Готовим демо-проект',
  message: 'Проект ещё наполняется. Откроется, как только будет готов',
  confirmLabel: 'Понятно',
  cancelLabel: null,
};

/**
 * Отказ сборки — единственный случай, где у окна есть второе действие: чинить копию логичнее там, куда
 * пользователь уже пришёл (он кликнул проект), а не в меню строки списка. Перезапуск идёт ВНУТРИ окна:
 * `onConfirm` сам блокирует кнопки, показывает `busyLabel` и закрывает окно, когда запрос отработал.
 */
function failedNotice(repair: () => Promise<void>): ConfirmDialogContext {
  return {
    title: 'Ошибка подготовки демо-проекта',
    message: 'Попробуйте ещё раз',
    confirmLabel: DEMO_RETRY_LABEL,
    busyLabel: 'Запускаем…',
    cancelLabel: 'Понятно',
    onConfirm: repair,
  };
}

/**
 * Ожидание демо-копии видно в списке проектов, но останавливает вход именно этот гейт: и клик по
 * карточке, и вставленная ссылка `/projects/<id>/albums` доходят сюда и получают один ответ —
 * окно с объяснением поверх списка, а не пустые экраны внутри недособранного проекта.
 *
 * Дёшево по построению: вердикт читается из уже загруженного списка, обычный проект уходит по `null`
 * без единого запроса. Список пуст только на холодном входе по прямому URL — там присоединяемся к его
 * вычитке. Не успела за бюджет навигации — проект открывается, окно и возврат к списку приходят поздно,
 * если человек всё ещё в этом проекте.
 */
export const demoProjectReadyGuard: CanActivateFn = async (route, routerState) => {
  const facade = inject(ProjectsFacade);
  const router = inject(Router);
  const opener = inject(HlmDialogOpener);
  const roles = inject(ProjectRoleService);
  const budget = inject(VerdictBudget);

  const projectId = route.paramMap.get('projectId');
  if (!projectId) return true;

  // У организатора этот гейт фотографский целиком: и возврат в кабинет, и починка демо-копии — не его
  // права. Его экран «Ученики» открывается как есть, а незавершённость проекта он видит на самом экране.
  if (roles.isProjectOrganizerOnly(projectId)) return true;

  const verdict = (async () => {
    if (facade.projects().length === 0) await facade.joinProjectsLoad();
    const project = facade.projectById(projectId);
    return project ? demoBuildState(project) : null;
  })();

  // Список не прочитался (сеть, боевой сбой) — вердикта нет, и гейт пропускает: он маршрутизатор UX,
  // а не защита. Fail-closed запер бы вход в ЛЮБОЙ проект на мигнувшей сети — ровно как в соседнем
  // `clientCabinetGuard`. Экраны внутри проекта свои отказы показывают сами.
  let settled: { value: DemoBuildState | null } | null;
  try {
    settled = await budget.within(verdict);
  } catch {
    return true;
  }

  const toList = router.createUrlTree(['/projects']);
  // Окно живёт в оверлее, а не в разметке маршрута, поэтому переживает редирект и остаётся поверх списка.
  const notify = (state: DemoBuildState): void => {
    opener.open<ConfirmDialogContext>(
      ConfirmDialogComponent,
      undefined,
      state === 'preparing'
        ? PREPARING_NOTICE
        : failedNotice(() => facade.retryDemoAttach(projectId).then(() => facade.loadProjects())),
    );
  };

  if (!settled) {
    budget.applyLate(
      verdict.then(state =>
        state === null
          ? null
          : () => {
              notify(state);
              budget.redirect(toList);
            },
      ),
      routerState.url,
    );
    return true;
  }

  if (settled.value === null) return true;
  notify(settled.value);
  return toList;
};
